import { type RootState, useAppSelector } from 'api/redux'
import { TrinityButton } from 'components/trinity-button'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { type Unit, type LabelType, LabelType as LabelTypeEnum } from 'types'
import { type ApiHandlerContext } from 'utils/useApiHandler'

interface ReprintProps {
  unit: Unit
  printLabelContext: ApiHandlerContext<LabelType>
}

interface GradeButton {
  labelName: string
  labelToPrint: LabelType
}

const initialLoadingState = { isLoading: false, labelName: '' }

export const Reprint = ({ unit, printLabelContext }: ReprintProps) => {
  const { t } = useTranslation()
  const [loadingState, setLoadingState] = useState(initialLoadingState)
  const printConfig = useAppSelector((state: RootState) => state.application.printConfig)

  const isApparelOrEquipment = () => {
    return unit.division === 'APPAREL' || unit.division === 'EQUIPMENT'
  }

  const isCGrade = () => {
    return unit.grade === 'C_GRADE'
  }

  const canPrintRfid = printConfig.includes(LabelTypeEnum.UpcRFID)

  const potentialButtons: GradeButton[] = [
    ...((!isCGrade() && canPrintRfid) ? [{ labelName: t('buttons.printing.rfid_label'), labelToPrint: LabelTypeEnum.UpcRFID }] : []),
    ...(isCGrade()
      ? [{ labelName: t('buttons.printing.c_grade_label'), labelToPrint: LabelTypeEnum.CGrade }]
      : isApparelOrEquipment()
        ? [{ labelName: t('buttons.printing.upc_label'), labelToPrint: LabelTypeEnum.UPC }]
        : [{ labelName: t('buttons.printing.shoebox_label'), labelToPrint: LabelTypeEnum.Shoebox }]
    )
  ]

  const printButtons = potentialButtons.filter(button =>
    printConfig?.includes(button.labelToPrint)
  )

  const handleOnClick = (type: LabelType) => {
    setLoadingState({ isLoading: true, labelName: type })
    printLabelContext.handleApiCall(type)
  }

  useEffect(() => {
    if (!printLabelContext.isLoading) {
      setLoadingState(initialLoadingState)
    }
  }, [printLabelContext.isLoading])

  return (
    <div className="flex flex-grow flex-col justify-center ml-6 gap-4">
      {printButtons.map((printButton) => (
        <TrinityButton
          key={printButton.labelName}
          loading={loadingState.labelName === printButton.labelToPrint && loadingState.isLoading}
          disabled={printLabelContext.isLoading}
          disabledMessage={t('buttons.printing.in_progress')}
          size={'massive'}
          onClick={() => { handleOnClick(printButton.labelToPrint) }}>
        <div className={'items-center flex flex-col'}>
          <img height={'80'} width={'80'} alt={'Printer icon'}
              src='/images/printer-icon.svg'
              className={'mb-4'}/>
          {t('buttons.printing.label', { labelType: printButton.labelName })}
        </div>
      </TrinityButton>
      ))}
    </div>
  )
}
